import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GlCateDetail, {type GlCateDetailRow} from '@/components/account/gl-invoice/mode/GlCateDetail';
import {useFsAdjustStore, useCompanyAnnualStore} from '@/store/audit';
import {
    fetchGlSheetAllRows,
    sumByFsCode,
} from '@/services/accountService/glInvoiceService';
import {resolveGlCurrentPeriodDisplay} from '@/utils/glToFsMapper';

// ==================== GL Invoice Detail Button ====================

export interface WpGlInvoiceDetailButtonProps {
    disabled?: boolean;
    onClick: () => void;
    /** 按钮文案，默认 "GL Detail"；分户时传账户名 */
    label?: string;
}

/**
 * GL Invoice Detail 触发按钮（WP 弹窗内 workflow === 'account' 时使用）
 */
export function WpGlInvoiceDetailButton({disabled, onClick, label = 'GL Detail'}: WpGlInvoiceDetailButtonProps) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<ReceiptLongIcon sx={{fontSize: 16}}/>}
            onClick={onClick}
            disabled={disabled}
            sx={{textTransform: 'none', fontSize: 12, maxWidth: 220}}
            title={label}
        >
            <Box component="span" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {label}
            </Box>
        </Button>
    );
}

// ==================== GL Invoice Detail Dialog ====================

export interface WpGlInvoiceDetailDialogProps {
    open: boolean;
    onClose: () => void;
    code: string;
    total: number;
    rows: GlCateDetailRow[];
    /** 分户展示时的账户名（覆盖 FS 科目名） */
    accountLabel?: string;
    /** 上年余额（分户或整科） */
    priorYearTotal?: number;
    companyId?: number;
    annualId?: number;
    /** 为 true 时从 GL Sheet 补算 Prior Year（扁平科目用） */
    loadPriorFromSheet?: boolean;
}

function ensurePriorYearBfRow(rows: GlCateDetailRow[], priorYearTotal?: number): GlCateDetailRow[] {
    if (priorYearTotal == null || priorYearTotal === 0) return rows;
    const hasPrior = rows.some((r) =>
        String(r.invoiceId || '').startsWith('__prior_bf__')
        || String(r.description || '').trim().startsWith('Prior Year b/f'),
    );
    if (hasPrior) return rows;
    return [
        {
            invoiceId: '__prior_bf__',
            date: null,
            description: 'Prior Year b/f +',
            voucherNo: null,
            invoiceNo: null,
            details: null,
            amount: priorYearTotal,
            counterparts: [],
        },
        ...rows,
    ];
}

/**
 * GL Invoice Detail 弹窗 — GlCateDetail 表格格式（含 Related Entries）
 */
export default function WpGlInvoiceDetailDialog({
    open,
    onClose,
    code,
    total,
    rows,
    accountLabel,
    priorYearTotal: priorYearProp,
    companyId,
    annualId,
    loadPriorFromSheet = false,
}: WpGlInvoiceDetailDialogProps) {
    const fsLabel = useMemo(() => {
        const line = useFsAdjustStore.getState().getLineByCode(code);
        return line?.name || code;
    }, [code]);

    const displayLabel = accountLabel?.trim() || fsLabel;

    const resolveFsLabel = useCallback((fsCode: string) => {
        const line = useFsAdjustStore.getState().getLineByCode(fsCode);
        return line?.name || null;
    }, []);

    const storeAnnualId = useCompanyAnnualStore((s) => s.currentAnnualId);
    const storeCompanyId = useCompanyAnnualStore((s) => {
        const id = s.currentAnnualId;
        return id ? s.byAnnualId[id]?.companyId : undefined;
    });
    const effectiveCompanyId = Number(companyId || storeCompanyId || 0) || 0;
    const effectiveAnnualId = Number(annualId || storeAnnualId || 0) || 0;

    const [sheetPrior, setSheetPrior] = useState<number | undefined>(undefined);
    const [sheetCurrent, setSheetCurrent] = useState<number | undefined>(undefined);
    const [loadingPrior, setLoadingPrior] = useState(false);

    useEffect(() => {
        if (!open || !loadPriorFromSheet || priorYearProp != null) {
            if (!open) {
                setSheetPrior(undefined);
                setSheetCurrent(undefined);
                setLoadingPrior(false);
            }
            return;
        }
        if (!effectiveCompanyId || !effectiveAnnualId) return;

        let cancelled = false;
        setLoadingPrior(true);
        fetchGlSheetAllRows(effectiveCompanyId, effectiveAnnualId)
            .then((res) => {
                if (cancelled) return;
                const prior = sumByFsCode(res.summaryPriorYear || {}, code);
                setSheetPrior(prior !== 0 ? prior : undefined);
                setSheetCurrent(resolveGlCurrentPeriodDisplay(
                    code,
                    res.summaryCurrentPeriod || {},
                    res.summaryPriorYear || {},
                    res.columnDefs || [],
                ));
            })
            .catch(() => {
                if (!cancelled) {
                    setSheetPrior(undefined);
                    setSheetCurrent(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPrior(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, loadPriorFromSheet, priorYearProp, effectiveCompanyId, effectiveAnnualId, code]);

    const priorYearTotal = priorYearProp ?? sheetPrior;
    const displayRows = useMemo(
        () => ensurePriorYearBfRow(rows, priorYearTotal),
        [rows, priorYearTotal],
    );
    // 有上年行时 Current 至少显示期末（total 或 prior），避免空白
    const columnTotal = (() => {
        if (accountLabel) {
            if (total !== 0) return total;
            if (priorYearTotal != null && priorYearTotal !== 0) return priorYearTotal;
            return total;
        }
        if (sheetCurrent != null) return sheetCurrent;
        return total;
    })();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="h6">GL Invoice Detail — {displayLabel}</Typography>
                    <IconButton size="small" onClick={onClose}><CloseIcon/></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loadingPrior ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress size={28}/>
                    </Box>
                ) : (
                    <GlCateDetail
                        label={displayLabel}
                        fsCode={code}
                        columnTotal={columnTotal}
                        priorYearTotal={priorYearTotal}
                        rows={displayRows}
                        loading={false}
                        resolveFsLabel={resolveFsLabel}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
